/**
 * DateRangePicker Component
 *
 * Provides quick select buttons (7d, 30d, This Month, Last Month)
 * and custom date range inputs for filtering data by date range.
 */

import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

type QuickOption = '7d' | '30d' | 'thisMonth' | 'lastMonth' | 'custom';

function getQuickRange(option: Exclude<QuickOption, 'custom'>): DateRange {
  const now = new Date();
  const today = now.toISOString().split('T')[0];

  switch (option) {
    case '7d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case '30d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    }
  }
}

function detectQuickOption(range: DateRange): QuickOption {
  const options: Exclude<QuickOption, 'custom'>[] = ['7d', '30d', 'thisMonth', 'lastMonth'];
  for (const option of options) {
    const quickRange = getQuickRange(option);
    if (quickRange.startDate === range.startDate && quickRange.endDate === range.endDate) {
      return option;
    }
  }
  return 'custom';
}

export function DateRangePicker({ value, onChange, className = '' }: DateRangePickerProps): JSX.Element {
  const [activeOption, setActiveOption] = useState<QuickOption>(() => detectQuickOption(value));
  const [customStart, setCustomStart] = useState(value.startDate);
  const [customEnd, setCustomEnd] = useState(value.endDate);

  useEffect(() => {
    setActiveOption(detectQuickOption(value));
    setCustomStart(value.startDate);
    setCustomEnd(value.endDate);
  }, [value]);

  const handleQuickSelect = (option: Exclude<QuickOption, 'custom'>) => {
    setActiveOption(option);
    onChange(getQuickRange(option));
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      setActiveOption('custom');
      onChange({ startDate: customStart, endDate: customEnd });
    }
  };

  const quickOptions: { key: Exclude<QuickOption, 'custom'>; label: string }[] = [
    { key: '7d', label: 'Last 7 Days' },
    { key: '30d', label: 'Last 30 Days' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
  ];

  return (
    <div className={`${className}`}>
      {/* Quick Select Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {quickOptions.map((option) => (
          <button
            key={option.key}
            onClick={() => handleQuickSelect(option.key)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              activeOption === option.key
                ? 'bg-evergreen text-white'
                : 'bg-soft-neutral text-ink hover:bg-slate-200'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Custom Date Range */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-muted mb-1">Start Date</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="input pl-9 py-1.5 text-sm w-40"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">End Date</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="input pl-9 py-1.5 text-sm w-40"
            />
          </div>
        </div>
        <button
          onClick={handleCustomApply}
          disabled={!customStart || !customEnd}
          className="btn-secondary btn-sm py-1.5"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default DateRangePicker;
